import React, { useState } from "react";
import styled from "styled-components";
import {
    AcceptIcon,
    Avatar,
    Button,
    Dialog,
    Flex,
    Loader,
    Text,
} from "@fluentui/react-northstar";
import { AppsListDetailFilled, MoneyHandFilled } from "@fluentui/react-icons";

import { useQuery } from "react-query";

import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Status, {
    get_theme_levels_color,
    LEVELS_BACKEND,
} from "../../components/Badges/Status";
import StatusFilled, { LEVELS } from "../../components/Badges/StatusFilled";
import { I18N_NS_EXPENSE, QUERY_KEY, TYPE_FORM } from "../../constants";
import { _api_get_detail_report_manager } from "../../api";
import { Container, ContainerFluid } from "../../../../common/styles";
import RowsExpenseDetail from "../../components/Tables/RowsExpenseDetail";
import RowTotalApproved from "../ExpensesDetailReport/RowTotalApproved";
import RowTotal from "../ExpensesDetailReport/RowTotal";
import RowCodeBudget from "../ExpensesDetailReport/RowCodeBudget";
import EvolutionCard from "../ExpensesDetailReport/EvolutionCard";
import EvolutionComment from "../ExpensesDetailReport/EvolutionComment";
import RefuseExpense from "./Dialogs/RefuseExpense";
import AcceptExpense from "./Dialogs/AcceptExpense";
import RowTotalRefused from "../ExpensesDetailReport/RowTotalRefused";
import { get_symbol_currency } from "../../functions";
import AcceptAnotherAmountExpense from "./Dialogs/AcceptAnotherAmount";
import EmbedDocument from "./EmbedDocument";
import { ROUTES } from "../../../../constants/routes";
import HeaderDetailExpenseReport from "../ExpensesDetailReport/HeaderDetailExpenseReport/HeaderDetailExpenseReport";
import StatusActionsDependRole, {
    STATUS_ROLE,
} from "../../components/Badges/StatusActionsDependRole";
import EmbedAndCommentsCard from "../../components/EmbedAndCommentsCard/EmbedAndCommentsCard";
import { useMediaQuery } from "react-responsive";
import { DEVICE, DEVICE_MAX, SIZES } from "../../../../constants/responsive";
import Reinvoiceable from "../../components/Badges/Reinvoiceable";
import HeaderDetail, {
    SButtonRefuseNote,
    SSplitButton,
    WrapperButtonActions,
} from "../../components/Header/HeaderDetail";
import TableRowExpense from "../../components/Tables/RowExpense/TableRowExpense";
import { useToastGlobal } from "../../../../stores/useToastGlobal";

const ExpensesDetailReportManager = () => {
    const { t } = useTranslation(I18N_NS_EXPENSE);
    const navigate = useNavigate();
    const globalToast = useToastGlobal();

    const { id_report, id_user } = useParams();

    // states
    const [toggleComment, setToggleComment] = useState(false);
    const [comments, setComments] = useState([]); // used for toggle between comments card.
    const [embed, setEmbed] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);

    // dialogs
    const [openAcceptAnotherAmount, setOpenAcceptAnotherAmount] =
        useState(false);
    const [openAcceptCurrentAmount, setOpenAcceptCurrentAmount] =
        useState(false);
    const [openRefuseExpense, setOpenRefuseExpense] = useState(false);

    const on_click_comment = (expense) => {
        const { histories, id } = expense;
        if (id === selectedExpense) {
            setToggleComment(false);
            setComments([]);
            setEmbed(null);
            setSelectedExpense(null);
            return;
        }
        setComments(histories);
        setToggleComment(false);
        setSelectedExpense(id);
        return;
    };

    const on_click_view_document = (url_document) => {
        if (embed === url_document) {
            setToggleComment(false);
            setEmbed(null);
            return;
        }
        return setEmbed(url_document);
    };

    const _goback = () => {
        return navigate(-1);
    };

    const {
        data: detail,
        isLoading,
        isFetching,
        refetch: refetch_detail_report,
    } = useQuery([QUERY_KEY + "::report_detail"], () =>
        _api_get_detail_report_manager({
            id_expense_report: id_report,
            id_user,
        })
    );

    const closeAllDialogs = () => {
        setOpenAcceptAnotherAmount(false);
        setOpenAcceptCurrentAmount(false);
        setOpenRefuseExpense(false);
    };

    const _onAcceptRefuseExpense = ([data, errors]) => {
        if (data?.success) {
            closeAllDialogs();
            globalToast.setShow({
                visible: true,
                content: errors,
                level: "success",
            });
            refetch_detail_report();
        } else {
            // show alert message here !
            globalToast.setShow({
                visible: true,
                content: errors,
                level: "danger",
            });
        }
        return;
    };

    const isMobileS = useMediaQuery({
        query: `(max-width: ${SIZES.mobileM})`,
    });
    const isLessThenTablet = useMediaQuery({
        query: DEVICE_MAX.tablet,
    });

    const isMobileM = useMediaQuery({
        query: `(max-width: ${SIZES.mobileL})`,
    });
    const isMobile = isMobileS || isMobileM;
    if (isLoading) {
        return <Loader />;
    }

    const {
        name,
        created_date,
        reinvoiceable,
        client,
        expenses,
        history,
        status,
        amount_total,
        currency,
        amount_approved,
        user,
        object_code,
    } = detail?.expense_report;

    const { textColor } = get_theme_levels_color(LEVELS_BACKEND[status?.value]); // this is used for showing colors : red, green, yellow

    const currency_symbol = get_symbol_currency(currency);

    const isExpenseToValidate =
        LEVELS_BACKEND[status?.value] === LEVELS.pending; // à valider
    const isDraft = LEVELS_BACKEND[status?.value] === LEVELS.draft; // brouillon

    return (
        <ContainerFluid styles={{ marginTop: "24px" }} column>
            {/* manager */}
            <HeaderDetail
                title={detail?.expense_report?.name}
                reinvoiceable={detail?.expense_report?.reinvoiceable}
                client_name={detail?.expense_report?.client?.name}
                objectCode={detail?.expense_report?.object_code?.name}
                disabledSendButton={true}
                showDotsMenu={false}
                showButtonSendNote={false}
                showSaveAuto={false}
                showButtonsAcceptReject={isExpenseToValidate}
                onClickAcceptNote={() => {
                    return setOpenAcceptCurrentAmount(true);
                }}
                onClickRejectNote={() => {
                    return setOpenRefuseExpense(true);
                }}
                onClickAcceptAnotherAmount={() => {
                    return setOpenAcceptAnotherAmount(true);
                }}
                onClickGoBack={_goback}
            />
            {isFetching && <Loader size={"smallest"} />}
            <ContainerStyle gap="gap.large">
                <Wrapper
                    fill
                    column
                    gap="gap.small"
                    styles={{
                        height: "fit-content",
                    }}
                >
                    <Flex vAlign="center" space="between" fill>
                        {/* header */}
                        <HeaderDetailExpenseReport
                            status={status}
                            user={user}
                            role={STATUS_ROLE.manager}
                        />
                        <StatusActionsDependRole
                            role={STATUS_ROLE.manager}
                            status={status?.value}
                        />
                        <Flex column hAlign="end">
                            <ButtonEvolution
                                content={history?.length}
                                icon={<AppsListDetailFilled />}
                                onClick={() => {
                                    setToggleComment((state) => !state);
                                    setComments([]);
                                    setEmbed(null);
                                    return;
                                }}
                                disabled={history?.length === 0}
                                iconPosition={"after"}
                                active={toggleComment}
                                text
                                primary
                                iconOnly
                            />
                        </Flex>
                    </Flex>
                    {/* white section */}
                    <TableRowExpense
                        IS_CAN_SEND={false}
                        detail={detail}
                        id_report={id_report}
                        REPORT_STATUS={detail?.expense_report?.status?.value}
                        isMobile={isMobile}
                        selectedExpense={selectedExpense}
                        LENGTH_EXPENSES={expenses.length}
                        handle_click_comment={on_click_comment}
                        on_click_view_document={on_click_view_document}
                        disableMoreItem={
                            detail?.expense_report?.status?.value !== 2
                        }
                        onClickTitle={({
                            id_expense,
                            form_type,
                            id_report,
                        }) => {
                            const expense_status =
                                detail?.expense_report?.status?.value;

                            if (expense_status === 2) {
                                // en cours de création
                                return navigate(
                                    ROUTES.expenses.update_form.custom_route(
                                        id_report,
                                        id_expense,
                                        TYPE_FORM[form_type]
                                    )
                                );
                            }
                        }}
                    />

                    {isExpenseToValidate && isLessThenTablet && (
                        <WrapperButtonActions vAlign="center" hAlign="center">
                            <SButtonRefuseNote
                                content={"Refuser la note"}
                                disabled={false}
                                secondary
                                flat
                                styles={{ color: "var(--color-danger)" }}
                                // loading={isSendingNote}
                                onClick={() => {
                                    return setOpenRefuseExpense(true);
                                }}
                            />
                            <div>
                                <SSplitButton
                                    menu={[
                                        {
                                            key: "accept_amount",
                                            content: "Accepter ce montant",
                                            icon: <AcceptIcon size="small" />,
                                            onClick: () => {
                                                return setOpenAcceptCurrentAmount(
                                                    true
                                                );
                                            },
                                            styles: {
                                                color: "var(--color-primary)",
                                            },
                                        },
                                        {
                                            key: "validate_another_amount",
                                            content:
                                                "Valider avec un autre montant",
                                            icon: <MoneyHandFilled />,
                                            onClick: () => {
                                                return setOpenAcceptAnotherAmount(
                                                    true
                                                );
                                            },
                                        },
                                    ]}
                                    button={{
                                        content: "Accepter la note",
                                        "aria-roledescription": "splitbutton",
                                        "aria-describedby":
                                            "instruction-message-primary-button",
                                        flat: true,
                                    }}
                                    toggleButton={{
                                        "aria-label": "plus d'options",
                                    }}
                                    onMainButtonClick={() => {
                                        return setOpenAcceptCurrentAmount(true);
                                    }}
                                    primary
                                    flat
                                    fluid
                                />
                            </div>
                        </WrapperButtonActions>
                    )}
                    <>
                        <RefuseExpense
                            isMobile={isMobile}
                            id_report={id_report}
                            id_user={user?.id}
                            open={openRefuseExpense}
                            onClose={() => {
                                return setOpenRefuseExpense(false);
                            }}
                            onAccept={_onAcceptRefuseExpense}
                        />
                        <AcceptExpense
                            isMobile={isMobile}
                            id_report={id_report}
                            id_user={user?.id}
                            amount={amount_total + " " + currency_symbol}
                            amount_number={amount_total}
                            onAccept={_onAcceptRefuseExpense}
                            client={client}
                            reinvoiceable={reinvoiceable}
                            open={openAcceptCurrentAmount}
                            onClose={() => {
                                return setOpenAcceptCurrentAmount(false);
                            }}
                        />
                        <AcceptAnotherAmountExpense
                            id_report={id_report}
                            id_user={user?.id}
                            amount={amount_total}
                            currency={currency_symbol}
                            reinvoiceable={reinvoiceable}
                            client={client}
                            onAccept={_onAcceptRefuseExpense}
                            open={openAcceptAnotherAmount}
                            onClose={() => {
                                return setOpenAcceptAnotherAmount(false);
                            }}
                        />
                    </>
                </Wrapper>
                {/* right section */}
                {toggleComment && (
                    <CommentCardFlex fill hAlign="center">
                        <EvolutionCard
                            history={history}
                            amount_approved={amount_approved}
                            currency={currency_symbol}
                            setToggleComment={setToggleComment}
                        />
                    </CommentCardFlex>
                )}

                <Flex fill>
                    {(comments.length > 0 || embed !== null) && (
                        <EmbedAndCommentsCard
                            comments={comments}
                            embed={embed}
                            onClose={() => {
                                setEmbed(null);
                                setComments([]);
                                setSelectedExpense(null);
                            }}
                        />
                    )}
                </Flex>
            </ContainerStyle>
        </ContainerFluid>
    );
};

export default ExpensesDetailReportManager;

const ExpensesCommentCard = styled(Flex)`
    @media ${DEVICE.mobileS} {
        width: 275px;
        margin: 0 auto;
    }
    @media ${DEVICE.mobileM} {
        width: 332px;
        margin: 0 auto;
    }
    @media ${DEVICE.mobileL} {
        width: 380px;
        margin: 0 auto;
    }
    @media ${DEVICE.tablet} {
    }
    @media ${DEVICE.laptop} {
        margin: 0;
    }
    @media ${DEVICE.laptopL} {
        margin: 0;
    }
`;

const CommentCardFlex = styled(Flex)`
    padding: 0px;
`;

const ContainerStyle = styled(Flex)`
    max-width: 1440px;
    margin: 0 auto;
    margin-top: 24px;

    @media ${DEVICE.mobileS || DEVICE.mobileM || DEVICE.mobileL} {
        width: 100%;
        flex-direction: column;
        padding: 0px;
        gap: 24px;
    }
    @media ${DEVICE.tablet} {
        flex-direction: column;
    }
    @media ${DEVICE.laptop} {
        flex-direction: row;
        gap: 6px;
        padding: 16px;
    }
    @media ${DEVICE.laptopL} {
        flex-direction: row;
        gap: 32px;
        padding: 16px;
    }
`;

const ButtonEvolution = styled(Button)`
    background-color: ${({ active }) => (!active ? "transparent" : "#EBEBEB")};
    padding-inline: 8px;
`;

const RefacturableClient = styled(Text)`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;

    color: #616161;
`;

const RefacturableText = styled(Text)`
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;

    color: #242424;
`;

const WhiteBoard = styled(Flex)`
    background-color: white;
    box-shadow: 0px 0.3px 0.9px rgba(0, 0, 0, 0.07),
        0px 1.6px 3.6px rgba(0, 0, 0, 0.11);
    border-radius: 8px;
`;

const FlexFooter = styled(Flex)`
    padding: 16px;
`;
const FlexBody = styled(Flex)``;
const FlexHeader = styled(Flex)``;

const CreatedAt = styled(Text)`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;

    display: flex;
    align-items: center;

    color: #616161;
`;

const Wrapper = styled(Flex)`
    width: 100%;
    @media ${DEVICE.laptop} {
        max-width: 850px;
        min-width: 850px;
    }
    @media ${DEVICE.laptopL} {
        max-width: 850px;
        min-width: 850px;
    }
`;

const Title = styled(Text)`
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;

    color: #242424;
`;
